import React, { useEffect, lazy, Suspense } from "react";

import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { logoutAction } from "./redux/slices/users/usersSlice";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import AuthRoute from "./components/AuthRoute/AuthRoute";
import AdminRoutes from "./components/AuthRoute/AdminRoutes";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import { ToastProvider } from "./components/Toast/ToastContext";
import { ThemeProvider } from "./components/Theme/ThemeContext";

// --- Lazy-loaded pages (cada una se descarga solo cuando el usuario navega ahí) ---

// Admin
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const ManageCoupons = lazy(() => import("./components/Admin/Coupons/ManageCoupons"));
const AddCoupon = lazy(() => import("./components/Admin/Coupons/AddCoupon"));
const UpdateCoupon = lazy(() => import("./components/Admin/Coupons/UpdateCoupon"));
const AddProduct = lazy(() => import("./components/Admin/Products/AddProduct"));
const ManageStocks = lazy(() => import("./components/Admin/Products/ManageStocks"));
const ProductUpdate = lazy(() => import("./components/Admin/Products/ProductUpdate"));
const ManageCategories = lazy(() => import("./components/Admin/Categories/ManageCategories"));
const ManageBrands = lazy(() => import("./components/Admin/Categories/ManageBrands"));

const CategoryToAdd = lazy(() => import("./components/Admin/Categories/CategoryToAdd"));
const AddCategory = lazy(() => import("./components/Admin/Categories/AddCategory"));
const AddBrand = lazy(() => import("./components/Admin/Categories/AddBrand"));
const AddStyle = lazy(() => import("./components/Admin/Categories/AddStyle"));
const UpdateCategory = lazy(() => import("./components/Admin/Categories/UpdateCategory"));
const BrandsList = lazy(() => import("./components/Admin/Categories/BrandsList"));
const StylesList = lazy(() => import("./components/Admin/Categories/StylesList"));
const OrdersList = lazy(() => import("./components/Admin/Orders/OrdersList"));
const ManageOrders = lazy(() => import("./components/Admin/Orders/ManageOrders"));
const UpdateOrders = lazy(() => import("./components/Admin/Orders/UpdateOrders"));
const Customers = lazy(() => import("./components/Admin/Orders/Customers"));

// Users
const Login = lazy(() => import("./components/Users/Forms/Login"));
const RegisterForm = lazy(() => import("./components/Users/Forms/RegisterForm"));
const CustomerProfile = lazy(() => import("./components/Users/Profile/CustomerProfile"));
const AddReview = lazy(() => import("./components/Users/Reviews/AddReview"));

// Products / cart
const Product = lazy(() => import("./components/Users/Products/Product"));
const ProductsFilters = lazy(() => import("./components/Users/Products/ProductsFilter"));
const ShoppingCart = lazy(() => import("./components/Users/Products/ShoppingCart"));
const OrderPayment = lazy(() => import("./components/Users/Products/OrderPayment"));
const ThanksForOrdering = lazy(() => import("./components/Users/Products/ThanksForOrdering"));

// Home / categorías
const HomePage = lazy(() => import("./components/HomePage/HomePage"));
const AllCategories = lazy(() => import("./components/HomePage/AllCategories"));
const AllBrands = lazy(() => import("./components/HomePage/AllBrands"));

// Páginas estáticas
const BeerStyleGuide = lazy(() => import("./components/Pages/BeerStyles"));
const SecurityPolicy = lazy(() => import("./components/Pages/SecurityPolicy"));
const PrivacyPolicy = lazy(() => import("./components/Pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./components/Pages/TermsOfService"));
const CourseRegistration = lazy(() => import("./components/Pages/Courses"));
const NotFound = lazy(() => import("./components/Pages/NotFound"));
const RestaurantMenu = lazy(() => import("./components/Pages/RestaurantMenu"));
const RestaurantMenu2 = lazy(() => import("./components/Pages/RestaurantMenu2"));


const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          localStorage.removeItem("userInfo");
          dispatch(logoutAction());
          window.location.href = "/login";
        }
      } catch (error) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        dispatch(logoutAction());
      }
    }
  }, [dispatch]);

  return (
    <ThemeProvider>
    <ToastProvider>
    <BrowserRouter>
      <ScrollToTop />
      <Navbar />

      <Suspense fallback={<div className="flex justify-center items-center min-h-screen" />}>
        <Routes>
          {/* admin route */}
          <Route
            path="admin"
            element={
              <AdminRoutes>
                <AdminDashboard />
              </AdminRoutes>
            }
          >
            <Route
              path=""
              element={
                <AdminRoutes>
                  <OrdersList />
                </AdminRoutes>
              }
            />
            <Route
              path="add-product"
              element={
                <AdminRoutes>
                  <AddProduct />
                </AdminRoutes>
              }
            />
            <Route
              path="manage-products"
              element={
                <AdminRoutes>
                  <ManageStocks />
                </AdminRoutes>
              }
            />
            <Route
              path="products/edit/:id"
              element={
                <AdminRoutes>
                  <ProductUpdate />
                </AdminRoutes>
              }
            />
            <Route
              path="add-coupon"
              element={
                <AdminRoutes>
                  <AddCoupon />
                </AdminRoutes>
              }
            />
            <Route path="manage-coupon" element={<ManageCoupons />} />
            <Route
              path="manage-coupon/edit/:code"
              element={
                <AdminRoutes>
                  <UpdateCoupon />
                </AdminRoutes>
              }
            />
            <Route
              path="category-to-add"
              element={
                <AdminRoutes>
                  <CategoryToAdd />
                </AdminRoutes>
              }
            />
            <Route path="add-category" element={<AddCategory />} />
            <Route
              path="manage-category"
              element={
                <AdminRoutes>
                  <ManageCategories />
                </AdminRoutes>
              }
            />
            <Route
              path="edit-category/:id"
              element={
                <AdminRoutes>
                  <UpdateCategory />
                </AdminRoutes>
              }
            />
            <Route
              path="add-brand"
              element={
                <AdminRoutes>
                  <AddBrand />
                </AdminRoutes>
              }
            />
            <Route path="brands-list" element={<BrandsList />} />
            <Route path="manage-brands" element={<AdminRoutes><ManageBrands /></AdminRoutes>} />
            <Route
              path="add-style"
              element={
                <AdminRoutes>
                  <AddStyle />
                </AdminRoutes>
              }
            />
            <Route path="all-styles" element={<StylesList />} />
            <Route path="manage-orders" element={<ManageOrders />} />
            <Route
              path="orders/:id"
              element={
                <AdminRoutes>
                  <UpdateOrders />
                </AdminRoutes>
              }
            />
            <Route
              path="customers"
              element={
                <AdminRoutes>
                  <Customers />
                </AdminRoutes>
              }
            />
          </Route>

          {/* public links */}
          <Route path="/" element={<HomePage />} />
          <Route path="/products-filters" element={<ProductsFilters />} />
          <Route path="/products/:id" element={<Product />} />
          <Route path="/all-categories" element={<AllCategories />} />
          <Route path="/all-brands" element={<AllBrands />} />
          <Route path="/success" element={<ThanksForOrdering />} />
          <Route path="/menu" element={<RestaurantMenu />} />
          <Route path="/menu_b" element={<RestaurantMenu2 />} />
          <Route
            path="/add-review/:id"
            element={
              <AuthRoute>
                <AddReview />
              </AuthRoute>
            }
          />
          <Route path="/shopping-cart" element={<ShoppingCart />} />
          <Route
            path="/order-payment"
            element={
              <AuthRoute>
                <OrderPayment />
              </AuthRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterForm />} />
          <Route
            path="/customer-profile"
            element={
              <AuthRoute>
                <CustomerProfile />
              </AuthRoute>
            }
          ></Route>
          <Route
            path="/style-guide"
            element={
              <div key={window.location.pathname}>
                <BeerStyleGuide />
              </div>
            }
          />
          <Route path="/courses" element={<CourseRegistration />} />
          <Route path="/security-policy" element={<SecurityPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <Footer />
      <CookieConsent />
    </BrowserRouter>
    </ToastProvider>
    </ThemeProvider>
  );
};

export default App;